import { clsx } from 'clsx'
import { Link as RouterLink } from 'react-router-dom'

import { SystemSpaceSlug } from '@bettermode/common/space'
import { QueryTagsArgs, Tag } from '@tribeplatform/gql-client/types'
import { TagPillWithLink } from '@tribeplatform/react-components/common/components'
import { T } from '@tribeplatform/react-components/i18n'
import {
  useSpace,
  useSpaceTags,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { useSlate } from '@tribeplatform/react-slate-kit/hooks'
import { BC } from '@tribeplatform/react-slate-kit/types'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { useQuery } from '../../hooks/useQuery.js'
import { EmptyState } from './EmptyState.js'
import { TagsBlockProps } from './types.js'

export const TagsBlock: BC<TagsBlockProps> = props => {
  const {
    title,
    limit,
    style,
    action,
    source,
    tagIds,
    spaceId: paramSpaceId,
    ...rest
  } = props

  const {
    context: { spaceId: contextSpaceId },
  } = useSlate()
  const spaceId = paramSpaceId || contextSpaceId

  const { data: space } = useSpace({
    variables: { id: spaceId },
    useQueryOptions: {
      enabled: !!spaceId,
    },
  })

  const decisionTagFilter = false

  let variables: QueryTagsArgs
  switch (source) {
    case 'specific':
      variables = {
        ids: tagIds,
        limit: 100,
      }
      break
    case 'most-popular':
    default:
      variables = {
        limit,
      }
      break
  }

  const { data, isInitialLoading, isFetched } = useSpaceTags({
    variables: {
      spaceId,
      ...variables,
    },
    useInfiniteQueryOptions: {
      enabled: !!spaceId,
    },
  })
  const { nodes: tags } = simplifyPaginatedResult<Tag>(data)

  const { tag: activeTagSlug, tag_id: activeTagId } = useQuery() || {}

  const { generatePath } = useSpaceUtil()

  const listClsx = [
    style === 'cloud' && 'flex flex-wrap gap-2',
    style === 'list' && 'flex flex-col gap-2',
  ]
  const itemClsx = [
    style === 'cloud' && 'break-words min-w-0',
    style === 'list' && 'flex justify-between space-s-5',
  ]

  const tagLink = (tag: Tag) => {
    if (action === 'filter' || !decisionTagFilter) {
      return tag.slug === activeTagSlug || tag.id === activeTagId
        ? generatePath(space)
        : generatePath(space, null, { tag_id: tag.id })
    }

    const queryParams: Record<string, string> = {
      query: '',
      tag: tag.title,
      type: 'post',
      space: spaceId,
    }
    return generatePath(SystemSpaceSlug.SEARCH, null, queryParams)
  }

  if (!isFetched || isInitialLoading) {
    return null
  }

  if (!tags?.length) {
    return <EmptyState {...props} />
  }

  const tagFilterEnabled = !!activeTagSlug || !!activeTagId
  return (
    <Card {...rest}>
      {(title || tagFilterEnabled) && (
        <Card.Header
          title={title}
          size="sm"
          action={
            action === 'filter' && tagFilterEnabled ? (
              <Link as={RouterLink} variant="accent" to={generatePath(space)}>
                <T defaultMessage="Clear" id="Generics.Clear.Verb" />
              </Link>
            ) : null
          }
        />
      )}
      <Card.Content>
        <div className={clsx(listClsx)}>
          {tags.map(tag => (
            <div key={tag.id} className={clsx(itemClsx)}>
              <TagPillWithLink
                className="min-w-0"
                active={
                  action === 'filter' &&
                  (tag.id === activeTagId || tag.slug === activeTagSlug)
                }
                link={() => tagLink(tag)}
                title={tag.title}
              />
            </div>
          ))}
        </div>
      </Card.Content>
    </Card>
  )
}
